import axios from 'axios';
import { loginRequest } from '../authConfig';

/**
 * Get an Access Token for Microsoft Graph API with Auto-Refresh
 */
export async function getAccessToken(msalInstance) {
    try {
        const account = msalInstance.getActiveAccount();
        if (!account) throw new Error("No active account found. Please log in.");

        const response = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: account
        });

        return response.accessToken;
    } catch (error) {
        console.error("Error acquiring token silently", error);
        if (error.name === "InteractionRequiredAuthError") {
            console.warn("User interaction required for token refresh. Redirecting...");
            await msalInstance.acquireTokenRedirect(loginRequest);
        }
        throw error;
    }
}

/**
 * Get a user's profile picture
 */
export async function getUserProfilePicture(msalInstance, userEmail) {
    try {
        const token = await getAccessToken(msalInstance);

        // Fetch user ID by email
        const userResponse = await axios.get(`https://graph.microsoft.com/v1.0/users?$filter=mail eq '${userEmail}'`, {
            headers: { Authorization: `Bearer ${token}` }
        });

        if (userResponse.data.value.length === 0) {
            throw new Error("User not found in directory.");
        }

        const userId = userResponse.data.value[0].id;

        // Fetch profile picture for the user
        const response = await axios.get(`https://graph.microsoft.com/v1.0/users/${userId}/photo/$value`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
        });

        return URL.createObjectURL(response.data);
    } catch (error) {
        console.warn("User profile picture not found", error);
        return null;
    }
}

/**
 * Check if a user exists in the organization
 */
export async function checkUserExists(msalInstance, userEmail) {
    try {
        const token = await getAccessToken(msalInstance);

        const response = await axios.get(`https://graph.microsoft.com/v1.0/users?$filter=mail eq '${userEmail}'`, {
            headers: { Authorization: `Bearer ${token}` }
        });

        return response.data.value.length > 0 ? response.data.value[0] : null;
    } catch (error) {
        console.error("Error checking user existence", error);
        throw error;
    }
}

/**
 * Get a user's groups
 */
export async function getUserGroups(msalInstance, userEmail) {
    try {
        const token = await getAccessToken(msalInstance);

        // Fetch user ID by email
        const userResponse = await axios.get(`https://graph.microsoft.com/v1.0/users?$filter=mail eq '${userEmail}'`, {
            headers: { Authorization: `Bearer ${token}` }
        });

        if (userResponse.data.value.length === 0) {
            throw new Error("User not found in directory.");
        }

        const userId = userResponse.data.value[0].id;

        // Fetch groups for the user
        const groupsResponse = await axios.get(`https://graph.microsoft.com/v1.0/users/${userId}/memberOf`, {
            headers: { Authorization: `Bearer ${token}` }
        });

        return groupsResponse.data.value;
    } catch (error) {
        console.error("Error fetching user groups", error);
        throw error;
    }
}

/**
 * Invite a user as a guest with a custom message
 */
export async function createGuestUser(msalInstance, email) {
    try {
        const token = await getAccessToken(msalInstance);

        // Custom invitation message
        const customized_message = "Willkommen bei PAUL Live! Sie erreichen das Portal mit den folgenden Link: https://paullive.tech";

        // Construct the invitation payload using your provided format
        const invitation = {
            "invitedUserEmailAddress": email,
            "inviteRedirectUrl": "https://paullive.tech",
            "sendInvitationMessage": true,
            "invitedUserMessageInfo": {
                "customizedMessageBody": customized_message
            }
        };

        // Send invitation request to Microsoft Graph API
        const response = await axios.post("https://graph.microsoft.com/v1.0/invitations", invitation, {
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
        });

        return response.data;
    } catch (error) {
        console.error("❌ Error creating guest user:", error);
        throw error;
    }
}
